import React, { useEffect, useState } from 'react';
import api from '../../services/api.js';
import { useToast } from '../../context/ToastContext.jsx';
import ImageField from './ImageField.jsx';

const asText = (arr) => (arr || []).join(', ');

function useSettings() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { api.settings().then(setData).catch((e) => setError(e.message)); }, []);
  return [data, setData, error];
}

export function SettingsPage() {
  const notify = useToast();
  const [data, , error] = useSettings();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!data) return;
    setF({
      announcement: data.announcement || '',
      contact: { email: '', phone: '', whatsapp: '', address: '', instagram: '', facebook: '', ...data.contact },
      freeOver: data.shipping.freeOver,
      fee: data.shipping.fee,
      clothing: asText(data.categories.clothing),
      handbags: asText(data.categories.handbags),
      sizeOptions: asText(data.sizeOptions),
    });
  }, [data]);

  if (error) return <p className="aerror">{error}</p>;
  if (!f) return <p className="amuted">Loading</p>;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const setC = (k, v) => setF((x) => ({ ...x, contact: { ...x.contact, [k]: v } }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.saveSettings({
        announcement: f.announcement,
        contact: f.contact,
        shipping: { freeOver: Number(f.freeOver), fee: Number(f.fee) },
        categories: { clothing: f.clothing, handbags: f.handbags },
        sizeOptions: f.sizeOptions,
      });
      notify('Settings saved', 'success');
    } catch (err) {
      notify(err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="aform">
      <div className="ahead"><h1 className="atitle">Settings</h1></div>

      <section className="acard">
        <h2>Announcement bar</h2>
        <label>Text shown at the top of every page (leave empty to hide)<input value={f.announcement} onChange={(e) => set('announcement', e.target.value)} maxLength={200} /></label>
      </section>

      <section className="acard">
        <h2>Shipping</h2>
        <div className="arow">
          <label>Shipping fee (Rs)<input type="number" min="0" value={f.fee} onChange={(e) => set('fee', e.target.value)} /></label>
          <label>Free shipping on orders over (Rs)<input type="number" min="0" value={f.freeOver} onChange={(e) => set('freeOver', e.target.value)} /></label>
        </div>
      </section>

      <section className="acard">
        <h2>Categories and sizes</h2>
        <label>Clothing sub categories (separate with commas)<input value={f.clothing} onChange={(e) => set('clothing', e.target.value)} /></label>
        <label>Handbag sub categories (separate with commas)<input value={f.handbags} onChange={(e) => set('handbags', e.target.value)} /></label>
        <label>Size options available when adding clothing<input value={f.sizeOptions} onChange={(e) => set('sizeOptions', e.target.value)} /></label>
      </section>

      <section className="acard">
        <h2>Contact details</h2>
        <div className="arow">
          <label>Email<input type="email" value={f.contact.email} onChange={(e) => setC('email', e.target.value)} /></label>
          <label>Phone<input value={f.contact.phone} onChange={(e) => setC('phone', e.target.value)} /></label>
          <label>WhatsApp number<input value={f.contact.whatsapp} onChange={(e) => setC('whatsapp', e.target.value)} placeholder="923001234567" /></label>
        </div>
        <label>Address<input value={f.contact.address} onChange={(e) => setC('address', e.target.value)} /></label>
        <div className="arow">
          <label>Instagram link<input value={f.contact.instagram} onChange={(e) => setC('instagram', e.target.value)} placeholder="https://instagram.com/yourpage" /></label>
          <label>Facebook link<input value={f.contact.facebook} onChange={(e) => setC('facebook', e.target.value)} placeholder="https://facebook.com/yourpage" /></label>
        </div>
      </section>

      <div className="asticky"><button type="submit" className="abtn" disabled={busy}>{busy ? 'Saving' : 'Save settings'}</button></div>
    </form>
  );
}

export function BannersPage() {
  const notify = useToast();
  const [data, , error] = useSettings();
  const [banners, setBanners] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (data) setBanners(data.banners || []); }, [data]);

  if (error) return <p className="aerror">{error}</p>;
  if (!banners) return <p className="amuted">Loading</p>;

  const patch = (i, p) => setBanners((b) => b.map((x, k) => (k === i ? { ...x, ...p } : x)));
  const move = (i, d) => setBanners((b) => {
    const n = [...b]; const j = i + d;
    if (j < 0 || j >= n.length) return b;
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const add = () => setBanners((b) => [...b, { id: `b${Date.now()}`, title: '', subtitle: '', cta: 'Shop now', link: '/shop', image: '', theme: 'dark' }]);

  const save = async () => {
    setBusy(true);
    try {
      await api.saveSettings({ banners });
      notify('Banners saved', 'success');
    } catch (err) { notify(err.message, 'error'); } finally { setBusy(false); }
  };

  return (
    <div className="aform">
      <div className="ahead">
        <h1 className="atitle">Banners</h1>
        <button type="button" className="abtn abtn--ghost" onClick={add} disabled={banners.length >= 8}>Add banner</button>
      </div>
      <p className="amuted">These rotate at the top of the home page. Use wide images, around 1920 by 900 pixels. Keep the important part of the picture away from the left and centre where the text sits.</p>

      {banners.length === 0 && <p className="amuted">No banners. The home page will show no hero section.</p>}
      {banners.map((b, i) => (
        <section className="acard" key={b.id}>
          <div className="ahead ahead--small">
            <h2>Banner {i + 1}</h2>
            <div className="arow arow--tight">
              <button type="button" className="abtn abtn--ghost" onClick={() => move(i, -1)} disabled={i === 0}>Move up</button>
              <button type="button" className="abtn abtn--ghost" onClick={() => move(i, 1)} disabled={i === banners.length - 1}>Move down</button>
              <button type="button" className="abtn abtn--ghost" onClick={() => setBanners((x) => x.filter((_, k) => k !== i))}>Remove</button>
            </div>
          </div>
          <ImageField multiple={false} value={b.image ? [b.image] : []} onChange={(v) => patch(i, { image: v[0] || '' })} />
          <div className="arow">
            <label>Title<input value={b.title} onChange={(e) => patch(i, { title: e.target.value })} maxLength={80} /></label>
            <label>Subtitle<input value={b.subtitle} onChange={(e) => patch(i, { subtitle: e.target.value })} maxLength={160} /></label>
          </div>
          <div className="arow">
            <label>Button text<input value={b.cta} onChange={(e) => patch(i, { cta: e.target.value })} maxLength={40} /></label>
            <label>Button link<input value={b.link} onChange={(e) => patch(i, { link: e.target.value })} placeholder="/shop/clothing" /></label>
            <label>Text colour
              <select value={b.theme} onChange={(e) => patch(i, { theme: e.target.value })}>
                <option value="dark">Dark text (for light images)</option>
                <option value="light">White text (for dark images)</option>
              </select>
            </label>
          </div>
        </section>
      ))}
      <div className="asticky"><button type="button" className="abtn" onClick={save} disabled={busy}>{busy ? 'Saving' : 'Save banners'}</button></div>
    </div>
  );
}
